/**
 * Hooks LeadBeton — Production journalière (responsable production).
 *
 * Saisie de la production réelle du jour → déstockage automatique des matières
 * premières, puis file de contrôle qualité avant entrée au parc.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  operationsApi,
  type DeclarerProductionPayload,
  type RejeterQcPayload,
  type ValiderQcParcPayload,
} from '@/api/operations';

const PROD_JOUR_KEY = ['operations', 'beton', 'production-journaliere'] as const;
const PARC_KEY      = ['operations', 'beton', 'parc']                   as const;
const LOTS_KEY      = ['operations', 'beton', 'lots']                   as const;
const ORDRES_KEY    = ['operations', 'beton', 'ordres']                 as const;
const MATIERES_KEY  = ['operations', 'beton', 'matieres']               as const;
const STOCKS_KEY    = ['operations', 'beton', 'stocks']                 as const;

/** Toutes les vues impactées par une déclaration de production. */
const invalidateAll = (qc: ReturnType<typeof useQueryClient>) => {
  [PROD_JOUR_KEY, PARC_KEY, LOTS_KEY, ORDRES_KEY, MATIERES_KEY, STOCKS_KEY].forEach((key) =>
    qc.invalidateQueries({ queryKey: key }),
  );
};

export const useProductionJournaliere = (date?: string) =>
  useQuery({
    queryKey:  [...PROD_JOUR_KEY, date ?? 'today'],
    queryFn:   () => operationsApi.beton.getProductionJournaliere(date),
    staleTime: 15_000,
  });

export const useDeclarerProduction = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: DeclarerProductionPayload) => operationsApi.beton.declarerProductionJournaliere(payload),
    onSuccess:  () => invalidateAll(qc),
  });
};

export const useValiderQcEtPlacer = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ValiderQcParcPayload) => operationsApi.beton.validerQcEtPlacer(payload),
    onSuccess:  () => invalidateAll(qc),
  });
};

export const useRejeterQcLots = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: RejeterQcPayload) => operationsApi.beton.rejeterQcLots(payload),
    onSuccess:  () => invalidateAll(qc),
  });
};
